import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  Button,
  Divider,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { getPostBySlug, formatDate } from '../data/posts';

const BlogPost = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (post) {
      document.title = `${post.title} — Teniqua Systems`;
    }
    return () => {
      document.title = 'Teniqua Systems';
    };
  }, [post]);

  if (!post) {
    return (
      <Container maxWidth="md" sx={{ py: 12, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Post not found
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          That article does not exist, or has moved.
        </Typography>
        <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate('/blog')}>
          Back to the blog
        </Button>
      </Container>
    );
  }

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: 8,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={formatDate(post.date)}
              size="small"
              sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
            <Chip
              label={`${post.readingMinutes} min read`}
              size="small"
              sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          </Box>
          <Typography variant="h1" gutterBottom sx={{ fontWeight: 700, fontSize: { xs: '2rem', md: '2.75rem' } }}>
            {post.title}
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            By {post.author}
          </Typography>
        </Container>
      </Box>

      {/* Article body */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 3 }}
        >
          All posts
        </Button>

        <Box component="article">
          {post.body.map((block, index) => {
            if (block.type === 'h2') {
              return (
                <Typography
                  key={index}
                  variant="h4"
                  component="h2"
                  sx={{ mt: 4, mb: 1.5, fontWeight: 600, fontSize: { xs: '1.4rem', md: '1.7rem' } }}
                >
                  {block.text}
                </Typography>
              );
            }
            return (
              <Typography
                key={index}
                variant="body1"
                paragraph
                sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.primary' }}
              >
                {block.text}
              </Typography>
            );
          })}
        </Box>

        <Divider sx={{ my: 5 }} />

        <Box
          sx={{
            backgroundColor: theme.palette.grey[100],
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Try CodePeek for yourself
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            It is free, runs in your browser, and never sends your scans anywhere.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<LaunchIcon />}
            onClick={() => window.open('https://codepeek.teniquasystems.com/', '_blank', 'noopener')}
          >
            Open CodePeek
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default BlogPost;
